import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Search, Send, Check, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useUserData } from './UserDataContext';
import { RoomService } from '../services/RoomService';
import { ROOM_MAX_MEMBERS } from '../services/roomLifecycle';

interface InviteToRoomModalProps {
    isOpen: boolean;
    onClose: () => void;
    roomId: string;
    roomTitle: string;
    inviterId: string;
    /** Uids already in the room, so they are not offered again. */
    memberIds: string[];
    seatsLeft: number;
}

/**
 * Invite friends into a room.
 *
 * The invite lands as the same groupInvite card tribes and forums send, and it
 * opens the room rather than joining it — the receiver still requests a seat and
 * the host or a mod still approves. That keeps one door into a room instead of
 * two, and means an invite cannot smuggle anyone past the capacity check or the
 * age bracket.
 *
 * Open to every member, not only the host: a room three people short should not
 * depend on one person's contact list.
 */
export function InviteToRoomModal({
    isOpen, onClose, roomId, roomTitle, inviterId, memberIds, seatsLeft,
}: InviteToRoomModalProps) {
    const { friends } = useUserData();
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!isOpen) { setSearch(''); setSelected([]); setSending(false); }
    }, [isOpen]);

    const candidates = (friends || []).filter((f: any) => {
        const id = String((f as any).uid || f.id);
        if (memberIds.includes(id)) return false;
        const q = search.toLowerCase();
        return !q
            || (f.name || '').toLowerCase().includes(q)
            || (f.username || '').toLowerCase().includes(q);
    });

    const toggle = (id: string) =>
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const send = async () => {
        if (selected.length === 0) return;
        setSending(true);
        try {
            const sent = await RoomService.inviteToRoom(roomId, inviterId, selected);
            if (sent > 0) {
                toast.success(`Invited ${sent} ${sent === 1 ? 'person' : 'people'}`, {
                    description: 'They still have to request a seat.',
                });
                onClose();
            } else {
                toast.error('Could not send those invites.');
            }
        } catch (e: any) {
            toast.error(e?.message || 'Could not send those invites.');
        } finally {
            setSending(false);
        }
    };

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0"
                        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
                    />
                    <motion.div
                        initial={{ scale: 0.94, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.94, opacity: 0, y: 20 }}
                        className="relative w-full max-w-md rounded-3xl overflow-hidden flex flex-col bg-white dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 shadow-2xl"
                        style={{ maxHeight: '80vh' }}
                    >
                        <div className="p-4 flex items-center justify-between border-b border-black/10 dark:border-white/10">
                            <div className="min-w-0">
                                <h2 className="text-lg text-black dark:text-white truncate" style={{ fontWeight: 700 }}>
                                    Invite to Room
                                </h2>
                                <p className="text-[#666666] dark:text-[#a0a0a0] truncate" style={{ fontSize: 11 }}>
                                    {roomTitle} · {seatsLeft} of {ROOM_MAX_MEMBERS} seats left
                                </p>
                            </div>
                            <Button
                                variant="ghost"
                                onClick={onClose}
                                className="w-9 h-9 p-0 rounded-full hover:bg-black/10 dark:hover:bg-white/10 shrink-0"
                            >
                                <X className="w-4 h-4 text-black dark:text-white" />
                            </Button>
                        </div>

                        <div className="p-4 border-b border-black/10 dark:border-white/10">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#8e8e8e' }} />
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search friends..."
                                    className="pl-9 bg-black/5 dark:bg-white/5 border-transparent focus:border-[#F39C12] rounded-xl"
                                />
                            </div>
                        </div>

                        <div
                            className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2"
                            style={{ minHeight: 0 }}
                        >
                            {candidates.length === 0 ? (
                                <div
                                    className="text-center text-[#666666] dark:text-[#a0a0a0]"
                                    style={{ paddingTop: 40, paddingBottom: 40 }}
                                >
                                    <UserPlus className="w-10 h-10 mx-auto mb-2" style={{ opacity: 0.25 }} />
                                    <p style={{ fontSize: 13, fontWeight: 700 }}>
                                        {search ? 'Nobody matches that' : 'Everyone you know is already here'}
                                    </p>
                                </div>
                            ) : (
                                candidates.map((f: any) => {
                                    const id = String(f.uid || f.id);
                                    const picked = selected.includes(id);
                                    return (
                                        <div
                                            key={id}
                                            onClick={() => toggle(id)}
                                            className="flex items-center gap-3 p-3 rounded-2xl cursor-pointer"
                                            style={{ background: picked ? 'rgba(68,120,234,0.14)' : 'transparent' }}
                                        >
                                            <Avatar className="w-10 h-10 shrink-0">
                                                <AvatarImage src={f.image || f.avatar || ''} />
                                                <AvatarFallback>{(f.name || '?')[0]}</AvatarFallback>
                                            </Avatar>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-black dark:text-white truncate" style={{ fontWeight: 700, fontSize: 14 }}>
                                                    {f.name}
                                                </p>
                                                <p className="text-[#666666] dark:text-[#a0a0a0] truncate" style={{ fontSize: 12 }}>
                                                    @{String(f.username || '').replace(/^@/, '')}
                                                </p>
                                            </div>
                                            <div
                                                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                                                style={{
                                                    border: picked ? 'none' : '1px solid #999',
                                                    background: picked ? '#4478ea' : 'transparent',
                                                }}
                                            >
                                                {picked && <Check className="w-3 h-3 text-white" />}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="p-4 border-t border-black/10 dark:border-white/10">
                            <Button
                                onClick={send}
                                disabled={selected.length === 0 || sending}
                                className="w-full h-11 rounded-xl bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md"
                                style={{ fontWeight: 700, opacity: selected.length === 0 || sending ? 0.5 : 1 }}
                            >
                                <span className="flex items-center justify-center gap-2">
                                    <Send className="w-4 h-4" />
                                    {sending ? 'Sending...' : `Invite ${selected.length || ''}`.trim()}
                                </span>
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body,
    );
}

export default InviteToRoomModal;
